import { Tool } from './tool';
import { EmbossedRectangle } from '../utils/embossed-rectangle';

export class ToolButton {
    private svgContent: string | null = null;
    private icon: HTMLImageElement | null = null;
    private cachedSvgImage: HTMLImageElement | null = null;
    private isLoadingSvg: boolean = false;

    constructor(private tool: Tool) {
        const iconPath = tool.getIconPath();
        if (iconPath) {
            this.loadIcon(iconPath);
        }
    }

    private loadIcon(path: string): void {
        if (path.toLowerCase().endsWith('.svg')) {
            // Load SVG file content
            fetch(path)
                .then(response => response.text())
                .then(svgContent => {
                    if (this.svgContent !== svgContent) {
                        this.svgContent = svgContent;
                        this.cachedSvgImage = null; // Invalidate cache when content changes
                        this.materializeSvg();
                    }
                })
                .catch(error => {
                    console.error('Error loading SVG:', error);
                });
        } else {
            // Load regular image
            this.icon = new Image();
            this.icon.src = path;
        }
    }

    private materializeSvg(): void {
        if (!this.svgContent || this.isLoadingSvg) return;

        this.isLoadingSvg = true;
        const img = new Image();
        const blob = new Blob([this.svgContent], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        
        img.onload = () => {
            URL.revokeObjectURL(url);
            this.cachedSvgImage = img;
            this.isLoadingSvg = false;
        };
        
        img.src = url;
    }

    public draw(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number): void {
        // Draw button background with embossed effect
        const cornerRadii = { topLeft: 4, topRight: 4, bottomRight: 4, bottomLeft: 4 };
        
        if (this.tool.isActive()) {
            // Pressed state - use solid color
            EmbossedRectangle.draw(ctx, x, y, width, height, cornerRadii, '#C0C0C0');
        } else {
            // Normal state - use gradient for 3D effect
            const gradient = ctx.createLinearGradient(x, y, x, y + height);
            gradient.addColorStop(0, '#FFFFFF');
            gradient.addColorStop(1, '#E8E8E8');
            EmbossedRectangle.draw(ctx, x, y, width, height, cornerRadii, gradient);
        }

        // Calculate icon position to center it
        const padding = 2;
        const iconSize = Math.min(width, height) - (padding * 2);
        const iconX = x + (width - iconSize) / 2;
        const iconY = y + (height - iconSize) / 2;

        // Check if icon path has changed
        const currentPath = this.tool.getIconPath();
        if (currentPath && currentPath !== this.lastIconPath) {
            this.loadIcon(currentPath);
            this.lastIconPath = currentPath;
        }

        // Draw icon if available
        if (this.cachedSvgImage) {
            ctx.drawImage(this.cachedSvgImage, iconX, iconY, iconSize, iconSize);
        } else if (this.icon && this.icon.complete) {
            try {
                ctx.drawImage(this.icon, iconX, iconY, iconSize, iconSize);
            } catch (e) {
                // If image is broken, draw a fallback
                this.drawFallback(ctx, x, y, width, height);
            }
        } else {
            this.drawFallback(ctx, x, y, width, height);
        }
    }

    private lastIconPath: string | undefined;

    private drawFallback(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number): void {
        // Draw a generic icon placeholder
        const padding = 5;
        const iconSize = Math.min(width, height) - (padding * 2);
        const centerX = x + width / 2;
        const centerY = y + height / 2;
        const boxSize = iconSize * 0.6;

        // Draw a box with a question mark
        ctx.strokeStyle = '#808080';
        ctx.lineWidth = 1;
        ctx.strokeRect(centerX - boxSize/2, centerY - boxSize/2, boxSize, boxSize);

        // Draw a question mark
        ctx.fillStyle = '#808080';
        ctx.font = `${boxSize * 0.6}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('?', centerX, centerY);
    }
} 
